import { z } from 'zod';
import type { Channel, ChatMessage, ConfigBackup, Panel, Presence, ServerSettings, User } from './models.js';

/**
 * Wire protocol over a single WebSocket.
 *
 *  Request : { id: number, method: string, params: unknown }
 *  Response: { id: number, ok: true, result: unknown } | { id: number, ok: false, error: string }
 *  Event   : { event: string, data: unknown }        (server → client, unsolicited)
 */

export const SWATCH = z.enum(['cyan', 'lime', 'orange', 'purple', 'steel', 'track', 'red', 'white']);
export const TALK_MODE = z.enum(['momentary', 'latch', 'both', 'none']);
export const LISTEN_MODE = z.enum(['toggle', 'always', 'none']);
export const CHANNEL_KIND = z.enum(['partyline', 'program', 'ifb']);
export const TARGET = z.string().regex(/^(ch|user):[A-Za-z0-9_-]{1,64}$/);

export const KeyConfigSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().max(24).nullable(),
  targets: z.array(TARGET).min(1).max(32),
  talk: TALK_MODE,
  listen: LISTEN_MODE,
  listenDefault: z.boolean(),
  color: SWATCH.nullable(),
  vox: z.boolean().default(false),
});

export const PanelSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  keys: z.array(KeyConfigSchema).max(128),
});

export const ChannelSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  short: z.string().min(1).max(6),
  kind: CHANNEL_KIND,
  color: SWATCH,
  dimDb: z.number().min(-100).max(0),
  programChannelId: z.string().nullable().default(null),
  order: z.number().int().min(0).max(10_000),
});

export const UserSchema = z.object({
  id: z.string().min(1).max(64),
  username: z
    .string()
    .min(2)
    .max(32)
    .regex(/^[a-z0-9._-]+$/i),
  displayName: z.string().min(1).max(48),
  role: z.enum(['admin', 'user']),
  panelId: z.string().nullable(),
  canFeedProgram: z.boolean(),
  /**
   * Service account (Audio Injector, hardware bridge): never uses one of the plan's seats, and
   * can listen and feed program audio but not talk from a mic.
   */
  service: z.boolean().default(false),
  color: SWATCH,
  createdAt: z.number(),
});

// ---- Client → server RPC params -------------------------------------------------------------

export const HelloParams = z.object({
  token: z.string().min(1),
  client: z.string().max(120).optional(),
});

export const TransportCreateParams = z.object({ direction: z.enum(['send', 'recv']) });
export const TransportConnectParams = z.object({ transportId: z.string(), dtlsParameters: z.any() });
export const ProduceParams = z.object({
  transportId: z.string(),
  kind: z.literal('audio'),
  rtpParameters: z.any(),
  appData: z.discriminatedUnion('role', [
    z.object({ role: z.literal('mic') }),
    z.object({ role: z.literal('program'), channelId: z.string() }),
  ]),
});
export const ProducerCloseParams = z.object({ producerId: z.string() });
export const ReadyParams = z.object({ rtpCapabilities: z.any() });
export const ConsumerReadyParams = z.object({ consumerId: z.string() });
export const TalkSetParams = z.object({ targets: z.array(TARGET).max(64), on: z.boolean() });
export const ListenSetParams = z.object({ targets: z.array(TARGET).max(64), on: z.boolean() });
export const CallSendParams = z.object({ target: TARGET });
export const ChatSendParams = z.object({ target: TARGET, text: z.string().min(1).max(2000) });
export const PingParams = z.object({ t: z.number() });
export const IngestOpenParams = z.object({ channelId: z.string(), stereo: z.boolean().default(false) });
export const IngestCloseParams = z.object({ id: z.string() });

export const AdminUserUpsertParams = z.object({
  user: UserSchema.partial({ id: true, createdAt: true }),
  password: z.string().min(4).max(128).optional(),
});
export const AdminIdParams = z.object({ id: z.string() });
export const AdminChannelUpsertParams = z.object({ channel: ChannelSchema.partial({ id: true }) });
export const AdminPanelUpsertParams = z.object({ panel: PanelSchema.partial({ id: true }) });
export const AdminSettingsParams = z.object({ serverName: z.string().min(1).max(48) });
export const AdminImportParams = z.object({ backup: z.any() });
export const AdminKickParams = z.object({ userId: z.string() });
export const AdminRecordStartParams = z.object({ channelId: z.string() });
export const AdminRecordStopParams = z.object({ recordingId: z.string() });
export const AdminRecordDeleteParams = z.object({ recordingId: z.string() });

/** Method name → params schema (server validates every call against this). */
export const RPC_SCHEMAS = {
  hello: HelloParams,
  'transport.create': TransportCreateParams,
  'transport.connect': TransportConnectParams,
  produce: ProduceParams,
  'producer.close': ProducerCloseParams,
  ready: ReadyParams,
  'consumer.ready': ConsumerReadyParams,
  'talk.set': TalkSetParams,
  'listen.set': ListenSetParams,
  'call.send': CallSendParams,
  'chat.send': ChatSendParams,
  ping: PingParams,
  'ingest.open': IngestOpenParams,
  'ingest.close': IngestCloseParams,
  'admin.status': z.object({}),
  'admin.panels.list': z.object({}),
  'admin.users.upsert': AdminUserUpsertParams,
  'admin.users.delete': AdminIdParams,
  'admin.channels.upsert': AdminChannelUpsertParams,
  'admin.channels.delete': AdminIdParams,
  'admin.panels.upsert': AdminPanelUpsertParams,
  'admin.panels.delete': AdminIdParams,
  'admin.settings.update': AdminSettingsParams,
  'admin.config.export': z.object({}),
  'admin.config.import': AdminImportParams,
  'admin.kick': AdminKickParams,
  'admin.record.start': AdminRecordStartParams,
  'admin.record.stop': AdminRecordStopParams,
  'admin.record.list': z.object({}),
  'admin.record.delete': AdminRecordDeleteParams,
  'admin.workers.status': z.object({}),
} as const;

export type RpcMethod = keyof typeof RPC_SCHEMAS;
export type RpcParams<M extends RpcMethod> = z.infer<(typeof RPC_SCHEMAS)[M]>;

// ---- Results ---------------------------------------------------------------------------------

export interface HelloResult {
  serverName: string;
  user: User;
  panel: Panel | null;
  channels: Channel[];
  users: User[];
  presence: Presence[];
  chat: ChatMessage[];
  /** mediasoup RouterRtpCapabilities (opaque to the protocol). */
  routerRtpCapabilities: unknown;
}

export interface TransportParams {
  id: string;
  iceParameters: unknown;
  iceCandidates: unknown;
  dtlsParameters: unknown;
}

export interface IngestInfo {
  /** Producer id — pass to ingest.close. */
  id: string;
  /** Address to send RTP to (the server's announced address). */
  ip: string;
  port: number;
  payloadType: number;
  ssrc: number;
}

export interface PeerStatus {
  userId: string;
  username: string;
  client: string;
  connectedAt: number;
  producers: number;
  consumers: number;
  transports: number;
}

export interface WorkerStatus {
  index: number;
  pid: number;
  port: number;
  peers: number;
}

export interface AdminStatus {
  serverName: string;
  uptimeSec: number;
  node: string;
  mediasoupVersion: string;
  workerPid: number;
  announcedIp: string;
  rtcPort: number;
  peers: PeerStatus[];
  memoryMb: number;
  workers: WorkerStatus[];
  seats: SeatStatus;
}

/** The plan's limit on people online at once, and how many seats are taken. */
export interface SeatStatus {
  /** 0 = unlimited. */
  maxUsers: number;
  inUse: number;
  plan: string | null;
}

// ---- Events (server → client) ---------------------------------------------------------------

export interface ConsumerNewEvent {
  id: string;
  producerId: string;
  kind: 'audio';
  rtpParameters: unknown;
  peerId: string;
  role: 'mic' | 'program';
  channelId?: string;
}
export interface ConsumerRouteEvent {
  id: string;
  targets: string[];
}
export interface ConsumerClosedEvent {
  id: string;
}
export interface PresenceEvent {
  list: Presence[];
}
export interface PresenceUpdateEvent {
  p: Presence;
}
export interface CallEvent {
  from: string;
  target: string;
}
export interface ChatEvent {
  message: ChatMessage;
}
export interface ConfigEvent {
  serverName: string;
  user: User;
  panel: Panel | null;
  channels: Channel[];
  users: User[];
}
export interface KickedEvent {
  reason: 'another-session' | 'admin' | 'user-deleted' | 'plan-limit';
}

export interface ServerEvents {
  'consumer.new': ConsumerNewEvent;
  'consumer.route': ConsumerRouteEvent;
  'consumer.closed': ConsumerClosedEvent;
  presence: PresenceEvent;
  'presence.update': PresenceUpdateEvent;
  call: CallEvent;
  chat: ChatEvent;
  config: ConfigEvent;
  kicked: KickedEvent;
}
export type ServerEventName = keyof ServerEvents;

export type { ConfigBackup, ServerSettings };
export type { Recording, RecordingState, RecordingTrack } from './models.js';
